import {
    Badge,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Flex,
    HStack,
    Icon,
    SimpleGrid,
    Text,
    VStack,
    forwardRef,
} from '@chakra-ui/react';
import { LuLock } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription, AlertIcon, Heading } from '@/components/v4';
import { Card, CardBody, CardHeader } from '@/components/v4/Card';

import {
    accessLevels,
    breadcrumbItems,
    implementationSteps,
    restrictionScenarios,
} from './constants';

const H3 = forwardRef((props, ref) => (
    <Heading
        as="h3"
        fontSize="24px"
        fontWeight="semibold"
        ref={ref}
        {...props}
    />
));

const H4 = forwardRef((props, ref) => (
    <Heading
        as="h4"
        fontSize="14px"
        fontWeight="semibold"
        ref={ref}
        {...props}
    />
));

const VisibilityGroupErrorPage = () => {
    const { darkMode } = useSelector((state) => state.settings);
    const bgColor = darkMode ? '#121212' : '#f5f5f5';
    const cardBg = darkMode ? 'dark.tcap' : null;
    const textColor = darkMode ? 'white' : '#737373';
    const borderColor = darkMode ? 'gray.200' : 'gray.600';
    const highlightBg = darkMode ? 'rgba(144, 205, 244, 0.16)' : 'blue.100';
    const highlightBorder = darkMode ? 'blue.600' : 'blue.200';
    const textBadgeColor = darkMode ? 'white' : 'black';

    return (
        <Box bg={bgColor} minH="100vh">
            <VStack spacing={6} align="stretch">
                {/* Breadcrumb */}
                <Breadcrumb fontWeight="medium" fontSize="sm">
                    {breadcrumbItems.map((crumb, index) => (
                        <BreadcrumbItem key={index} isCurrentPage={!crumb.href}>
                            {crumb.href ? (
                                <BreadcrumbLink as={Link} to={crumb.href}>
                                    {crumb.label}
                                </BreadcrumbLink>
                            ) : (
                                <Text>{crumb.label}</Text>
                            )}
                        </BreadcrumbItem>
                    ))}
                </Breadcrumb>

                {/* Header Section */}
                <Flex justifyContent="space-between" alignItems="center">
                    <Box>
                        <Heading size="xl" mb={2}>
                            Visibility Groups
                        </Heading>
                        <Text color={textColor} fontSize="md">
                            Advanced access control and data isolation -
                            Currently unavailable
                        </Text>
                    </Box>
                    <Badge
                        bg={highlightBg}
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm">
                        <HStack spacing={2}>
                            <Icon as={LuLock} color="#3182ce" boxSize={4} />
                            <Text
                                fontSize="xs"
                                textTransform="capitalize"
                                color={textBadgeColor}>
                                Feature Disabled
                            </Text>
                        </HStack>
                    </Badge>
                </Flex>

                {/* Info Alert */}
                <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                        The Visibility Groups feature is not currently enabled
                        for your account.
                    </AlertDescription>
                </Alert>

                {/* Content Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Left Column - Access Levels */}
                    <Card bg={cardBg} borderColor={borderColor}>
                        <CardHeader>
                            <H3>Default Access Hierarchy</H3>
                            <Text color={textColor} mt={1} fontSize="sm">
                                Standard administrator visibility levels
                            </Text>
                        </CardHeader>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {accessLevels.map((level, index) => (
                                    <Box
                                        key={index}
                                        bg={
                                            level.highlight
                                                ? highlightBg
                                                : 'transparent'
                                        }
                                        borderLeft="4px"
                                        borderColor="brand.500"
                                        borderWidth="4px"
                                        borderStyle="solid"
                                        borderTop="none"
                                        borderRight="none"
                                        borderBottom="none"
                                        p={level.highlight ? 4 : 0}
                                        pl={4}
                                        borderRadius={
                                            level.highlight ? 'md' : 'none'
                                        }>
                                        <HStack spacing={3} align="flex-start">
                                            <Box>
                                                <H4
                                                    mb={1}
                                                    color={
                                                        level.highlight
                                                            ? 'var(--chakra-colors-blue-500)'
                                                            : null
                                                    }>
                                                    {level.title}
                                                </H4>
                                                <Text
                                                    fontSize="sm"
                                                    color={textColor}>
                                                    {level.description}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* Right Column - Restriction Scenarios */}
                    <Card bg={cardBg} borderColor={borderColor}>
                        <CardHeader>
                            <H3>Restriction Scenarios</H3>
                            <Text color={textColor} mt={1} fontSize="sm">
                                When visibility groups become necessary
                            </Text>
                        </CardHeader>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                {restrictionScenarios.map((scenario, index) => (
                                    <Box
                                        key={index}
                                        borderLeft="4px"
                                        borderColor="brand.500"
                                        pl={4}>
                                        <H4 mb={1}>{scenario.title}</H4>
                                        <Text fontSize="sm" color={textColor}>
                                            {scenario.description}
                                        </Text>
                                    </Box>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Implementation Steps */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <H3>How Visibility Groups Work</H3>
                        <Text color={textColor} fontSize="sm" mt={1}>
                            Understanding the implemenation and controls
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <VStack spacing={4} align="stretch">
                            {implementationSteps.map((step, index) => (
                                <Box
                                    key={index}
                                    borderLeft="4px"
                                    borderColor="brand.500"
                                    pl={4}>
                                    <H4 mb={1}>{step.title}</H4>
                                    <Text fontSize="sm" color={textColor}>
                                        {step.description}
                                    </Text>
                                </Box>
                            ))}
                        </VStack>
                    </CardBody>
                </Card>

                {/* CTA Section */}
                <Card bg={highlightBg} borderColor={highlightBorder}>
                    <CardBody>
                        <VStack spacing={4} textAlign="center">
                            <Box
                                p={2}
                                display="flex"
                                alignItems="center"
                                justifyContent="center">
                                <H3 fontSize="20px">Need Visibility Groups?</H3>
                            </Box>
                            <Text
                                color={textColor}
                                fontSize="md"
                                maxW="2xl"
                                mx="auto">
                                If your organization requires restricted access
                                controls for security or compliance purposes,
                                contact support to discuss enabling Visibility
                                Groups for your Wholesaler account.
                            </Text>
                        </VStack>
                    </CardBody>
                </Card>
            </VStack>
        </Box>
    );
};

export default VisibilityGroupErrorPage;
